import { useCallback } from 'react'

import { getPrescriptionToastActions } from '../../../components/prescriptions/PrescriptionToastActions'
import { useUniqueToast } from '../../toasts/hooks/useUniqueToast'

export interface ShowSuccessToastOptions {
  products?: { sku: string }[]
  persist?: boolean
  hideLink?: boolean
  onLinkClick?: () => void
  message?: string
}

export interface UseAddToPrescriptionToast {
  showSuccessToast: (options?: ShowSuccessToastOptions) => void
  showErrorToast: (options?: ShowSuccessToastOptions) => void
}

const getSuccessMessage = (products?: { sku: string }[]): string => {
  if (products && products.length > 1) {
    return 'All items added to eScript'
  }
  return 'Item added to eScript'
}

export const useAddToPrescriptionToast = (): UseAddToPrescriptionToast => {
  const { showToast, hideToastById } = useUniqueToast({
    toastIdPrefix: 'add-to-prescription-',
  })

  const showSuccessToast = useCallback(
    ({
      products,
      persist = false,
      message: customMessage,
      hideLink = false,
      onLinkClick,
    }: ShowSuccessToastOptions = {}) => {
      const message = customMessage ?? getSuccessMessage(products)
      showToast(message, {
        variant: 'success',
        action: getPrescriptionToastActions({ dismissToast: hideToastById, hideLink, onLinkClick }),
        persist,
      })
    },
    [hideToastById, showToast],
  )

  const showErrorToast = useCallback(
    ({
      persist = false,
      message: customMessage,
      hideLink = false,
    }: ShowSuccessToastOptions = {}) => {
      const message = customMessage ?? 'There has been an unexpected error'
      showToast(message, {
        variant: 'error',
        action: getPrescriptionToastActions({ dismissToast: hideToastById, hideLink }),
        persist,
      })
    },
    [hideToastById, showToast],
  )

  return { showSuccessToast, showErrorToast }
}
